import React from "react";
import { AntSwitch } from "../../../../../Bilateral/LedgerViewContent/LegerViewType";
import SelectInput from "../../../../../../commons/Inputs/Select/SelectInput";
import DateInput from "../../../../../../commons/Inputs/DateInputBailateral/DateInput";
import TimeInput from "../../../../../../commons/Inputs/TimeInputBailateral/TimeInput";
import { EnergyRateList } from "../../../../../Modals/ConGen/ConGenListingModal";
import { formatDate, formatSlotTime } from "../../../../../../helpers/dateFormat";
import Styles from "../TermsCondition.module.css";

// Returns true when the two formatted values differ — used to highlight changed fields
const isDifferentValue = (value, compareWith) => value !== compareWith;

// Build the display string for an energy rate field
const formatEnergyRate = (rateType, cost1, cost2) => {
  if (rateType === "FIXED") return cost1 ?? cost2;
  if (rateType === "DAM_PLUS") return `DAM+${cost1 ?? cost2}`;
  return `DAM-${cost1 ?? cost2}`;
};

const CounterpartySection = ({
  item,
  selfSlot,
  matchSlotsCount,
  selectedSelfReq,
  isEditTerms,
  user,
  listingType,
  currentValues,
  fieldErrors,
  selectedBids,
  dataWithIndex,
  isAllSlotsSelected,
  isCompanyCollapsed,
  isRequisitionCollapsed,
  handleCompanyToggle,
  handleUserSelectBid,
  handleInputChange,
  handleEnergyRateTypeChange,
  setFieldErrors,
}) => {
  const isDisabled =
    (user === "consumer" && item.initiatorType === "generator") ||
    (user === "generator" && item.initiatorType === "consumer");
  const isCollapsed = isCompanyCollapsed(item.bidId, selectedSelfReq.id);

  // Date boundary constants (computed once per render)
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  const upto7 = new Date(today);
  upto7.setDate(today.getDate() + 6);
  const dayAfterTomorrow = new Date(today);
  dayAfterTomorrow.setDate(today.getDate() + 2);
  const next7EndMax = new Date(today);
  next7EndMax.setDate(today.getDate() + 6);
  const greaterthan7 = new Date(today);
  greaterthan7.setDate(today.getDate() + 4);

  return (
    <div key={item.bidId} className={Styles.tableDetailsMainContCustom}>
      {/* ── Company header row ─────────────────────────────────────── */}
      <div className={Styles.sectionBox}>
        <div
          style={{ backgroundColor: "#52888f", borderRadius: "20px" }}
          className={Styles.row}
        >
          <div
            style={{
              fontWeight: 600,
              fontSize: "12px",
              flex: "0 0",
              marginRight: "15px",
            }}
          >
            <span style={{ color: "white", textWrap: "nowrap" }}>
              {" "}
              {item.generatorName || item.consumerName}{" "}
            </span>
            {item.status === "TERMS_INITIATED" &&
              item.initiatorType !== user &&
              !isEditTerms && (
                <span
                  style={{
                    fontWeight: "500",
                    fontSize: "10px",
                    marginLeft: "8px",
                    color: "white",
                    textWrap: "nowrap",
                  }}
                >
                  (T&C Request Received)
                </span>
              )}
          </div>

          <div style={{ display: "flex", justifyContent: "end" }}>
            {!isEditTerms && (
              <AntSwitch
                checked={isAllSlotsSelected(item.bidId, selectedSelfReq.id)}
                onChange={() => handleCompanyToggle(item, selectedSelfReq.id)}
              />
            )}
          </div>
        </div>
      </div>

      {/* ── Match-slot rows (collapsible) ─────────────────────────── */}
      <div
        className={`${Styles.collapseContainer} ${
          isCollapsed ? Styles.collapsed : Styles.expanded
        }`}
      >
        <div className={Styles.collapseContent}>
          {item.matchSlots.map((matchSlot, idx) => {
            const isReqCollapsed = isRequisitionCollapsed(
              item.bidId,
              selectedSelfReq.id,
              matchSlot.id,
            );
            const isReqExpanded = !isReqCollapsed;

            const isReqSelected = selectedBids.some(
              (bid) =>
                bid.id === matchSlot.id &&
                bid.selfSlotId ===
                  dataWithIndex[0].matches[selectedSelfReq.id].selfSlot.id &&
                bid.matchIndex === selectedSelfReq.id,
            );

            const currentEnergyRateStr = currentValues[`req${idx + 1}_energyRateStr`];
            const currentEnergyRate = currentValues[`req${idx + 1}_energyRate`];

            const finalCurrentEnergyDisplay = formatEnergyRate(
              currentEnergyRateStr,
              currentEnergyRate,
              currentEnergyRate,
            );
            const matchSlotEnergyDisplay = formatEnergyRate(
              matchSlot?.energyRateType,
              matchSlot?.sellingCost,
              matchSlot?.landingCost,
            );
            const energyRateBorderHighlight =
              isDisabled && isDifferentValue(finalCurrentEnergyDisplay, matchSlotEnergyDisplay)
                ? "2px solid var(--primary-color-text)"
                : "";

            // Derive minDate / maxDate for start date picker
            const startDateMin =
              listingType === "INTRA_DAY"
                ? today
                : listingType === "NEXT_DAY"
                  ? tomorrow
                  : listingType === "UPTO_SEVEN_DAYS"
                    ? today
                    : listingType === "GREATER_THAN_SEVEN_DAYS"
                      ? today
                      : new Date();

            const startDateMax =
              listingType === "INTRA_DAY"
                ? today
                : listingType === "NEXT_DAY"
                  ? tomorrow
                  : listingType === "UPTO_SEVEN_DAYS"
                    ? upto7
                    : undefined;

            const endDateMin =
              listingType === "INTRA_DAY"
                ? today
                : listingType === "NEXT_DAY"
                  ? tomorrow
                  : listingType === "UPTO_SEVEN_DAYS"
                    ? dayAfterTomorrow
                    : listingType === "GREATER_THAN_SEVEN_DAYS"
                      ? today
                      : undefined;

            const endDateMax =
              listingType === "INTRA_DAY"
                ? today
                : listingType === "NEXT_DAY"
                  ? tomorrow
                  : listingType === "UPTO_SEVEN_DAYS"
                    ? next7EndMax
                    : undefined;

            const isDateDisabled =
              isDisabled ||
              item?.isIntraDay ||
              listingType === "INTRA_DAY" ||
              listingType === "NEXT_DAY";

            return (
              <div className={Styles.reqSection} key={matchSlot.id}>
                {/* Sub-header row for this requisition */}
                <div
                  style={{ backgroundColor: "#EFFCFF" }}
                  className={Styles.row}
                >
                  <div
                    style={{
                      fontWeight: 500,
                      fontSize: "12px",
                      color: "#34656D",
                      width: "90%",
                      flex: "0 0",
                      marginRight: "15px",
                      textWrap: "nowrap",
                    }}
                  >
                    {item.generatorName || item.consumerName} - Requisition{" "}
                    {idx + 1}
                  </div>

                  <div style={{ display: "flex", justifyContent: "end", flex: "0 0" }}>
                    {!isEditTerms && (
                      <AntSwitch
                        checked={isReqSelected}
                        onChange={() =>
                          handleUserSelectBid(matchSlot, item, selectedSelfReq.id)
                        }
                      />
                    )}
                  </div>
                </div>

                {/* Expandable field rows */}
                <div
                  className={`${Styles.reqContent} ${
                    isReqExpanded ? Styles.expanded : Styles.collapsed
                  }`}
                >
                  <>
                    {/* Energy Rate */}
                    <div className={Styles.row}>
                      <div
                        style={{
                          flexBasis: "191px",
                          minWidth: "191px",
                          maxWidth: "191px",
                          width: "191px",
                        }}
                      >
                        Energy Rate{" "}
                        <span
                          style={{ fontSize: "10px", color: "var(--fourth-color-text)" }}
                        >
                          (₹/kWh)
                        </span>
                      </div>

                      <div style={{ fontWeight: 500 }}>
                        {formatEnergyRate(
                          selfSlot.energyRateType,
                          selfSlot.sellingCost ?? selfSlot.landingCost ?? selfSlot.energyRate,
                          selfSlot.sellingCost ?? selfSlot.landingCost ?? selfSlot.energyRate,
                        )}
                      </div>

                      <div style={{ fontWeight: 500 }} className={Styles.greenText}>
                        {matchSlotEnergyDisplay}
                      </div>

                      <div style={{ position: "relative" }}>
                        <input
                          min={0}
                          style={{
                            width: "30%",
                            paddingLeft: "90px",
                            border: energyRateBorderHighlight,
                          }}
                          disabled={isDisabled}
                          type="number"
                          className={Styles.inputBox}
                          value={currentEnergyRate ?? ""}
                          onChange={(e) =>
                            handleInputChange(
                              e,
                              `req${idx + 1}_energyRate`,
                              item,
                              matchSlotsCount,
                            )
                          }
                          placeholder="Enter rate"
                        />

                        <div
                          style={{
                            position: "absolute",
                            left: "3px",
                            top: energyRateBorderHighlight ? "9px" : "8px",
                          }}
                        >
                          <SelectInput
                            showExample={false}
                            style={{
                              width: "85px",
                              borderRadius: "5px",
                              border: "1px solid var(--primary-color-text)",
                              backgroundColor: "#fffaf6",
                              color: "var(--primary-color-text)",
                              padding: "0px",
                            }}
                            list={EnergyRateList?.map((e) => e.name)}
                            example={"Select Type"}
                            onChange={(selectedName) => {
                              const selectedItem = EnergyRateList.find(
                                (e) => e.name === selectedName,
                              );
                              const energyRateType =
                                selectedItem?.name === "Fixed"
                                  ? "FIXED"
                                  : selectedItem?.name === "DAM+"
                                    ? "DAM_PLUS"
                                    : "DAM_MINUS";
                              handleEnergyRateTypeChange(energyRateType, item, selectedSelfReq.id);
                            }}
                            value={
                              currentEnergyRateStr === "DAM_PLUS"
                                ? "DAM+"
                                : currentEnergyRateStr === "DAM_MINUS"
                                  ? "DAM-"
                                  : "Fixed"
                            }
                            height={24}
                            disabled={isDisabled}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Quantity */}
                    <div className={Styles.row}>
                      <div
                        style={{
                          flexBasis: "191px",
                          minWidth: "191px",
                          maxWidth: "191px",
                          width: "191px",
                        }}
                      >
                        Quantity{" "}
                        <span
                          style={{ fontSize: "10px", color: "var(--fourth-color-text)" }}
                        >
                          (MW)
                        </span>
                      </div>
                      <div style={{ fontWeight: 500 }}>
                        {selfSlot.sellEnergy || selfSlot.purchaseEnergy || selfSlot.quantity}
                      </div>
                      <div style={{ fontWeight: 500 }} className={Styles.greenText}>
                        {matchSlot.sellEnergy || matchSlot.purchaseEnergy}
                      </div>
                      <div>
                        <input
                          style={{
                            border:
                              isDisabled &&
                              (matchSlot.sellEnergy || matchSlot.purchaseEnergy) !==
                                Number(currentValues[`req${idx + 1}_quantity`])
                                ? "2px solid var(--primary-color-text)"
                                : "",
                          }}
                          min={0}
                          disabled={isDisabled}
                          type="number"
                          className={Styles.inputBox}
                          value={currentValues[`req${idx + 1}_quantity`]}
                          onChange={(e) =>
                            handleInputChange(e, `req${idx + 1}_quantity`, item, matchSlotsCount)
                          }
                        />
                      </div>
                    </div>

                    {/* Start Date */}
                    <div className={Styles.row}>
                      <div
                        style={{
                          flexBasis: "191px",
                          minWidth: "191px",
                          maxWidth: "191px",
                          width: "191px",
                        }}
                      >
                        Start Date
                      </div>
                      <div>{formatDate(selfSlot.dateStartAt)}</div>
                      <div>{formatDate(matchSlot.dateStartAt)}</div>
                      <div style={{ display: "flex", justifyContent: "end" }}>
                        <DateInput
                          usePortal={true}
                          style={{
                            width: "94%",
                            paddingLeft: "15px",
                            border:
                              isDisabled &&
                              matchSlot.dateStartAt !==
                                currentValues[`req${idx + 1}_startDate`]
                                ? "2px solid var(--primary-color-text)"
                                : "",
                          }}
                          value={currentValues[`req${idx + 1}_startDate`] || ""}
                          disabled={isDateDisabled}
                          onChange={(e) =>
                            handleInputChange(e, `req${idx + 1}_startDate`, item, matchSlotsCount)
                          }
                          minDate={startDateMin}
                          maxDate={startDateMax}
                          height={32}
                        />
                      </div>
                    </div>

                    {/* End Date */}
                    <div className={Styles.row}>
                      <div
                        style={{
                          flexBasis: "191px",
                          minWidth: "191px",
                          maxWidth: "191px",
                          width: "191px",
                        }}
                      >
                        End Date
                      </div>
                      <div>{formatDate(selfSlot.dateEndAt)}</div>
                      <div>{formatDate(matchSlot.dateEndAt)}</div>
                      <div style={{ display: "flex", justifyContent: "end" }}>
                        <DateInput
                          value={currentValues[`req${idx + 1}_endDate`] || ""}
                          usePortal={true}
                          style={{
                            width: "94%",
                            paddingLeft: "15px",
                            border:
                              isDisabled &&
                              matchSlot.dateEndAt !== currentValues[`req${idx + 1}_endDate`]
                                ? "2px solid var(--primary-color-text)"
                                : "",
                          }}
                          disabled={isDateDisabled}
                          minDate={endDateMin}
                          maxDate={endDateMax}
                          onChange={(e) =>
                            handleInputChange(e, `req${idx + 1}_endDate`, item, matchSlotsCount)
                          }
                          height={32}
                        />
                      </div>
                    </div>

                    {/* Start Time */}
                    <div className={Styles.row}>
                      <div
                        style={{
                          flexBasis: "191px",
                          minWidth: "191px",
                          maxWidth: "191px",
                          width: "191px",
                        }}
                      >
                        Start Time
                      </div>
                      <div>{formatSlotTime(selfSlot.slotStartAt)}</div>
                      <div>{formatSlotTime(matchSlot.slotStartAt)}</div>
                      <div style={{ display: "flex", justifyContent: "end" }}>
                        <TimeInput
                          style={{
                            width: "98%",
                            border:
                              isDisabled &&
                              String(formatSlotTime(matchSlot.slotStartAt)) !==
                                String(
                                  formatSlotTime(currentValues[`req${idx + 1}_startTime`]),
                                )
                                ? "2px solid var(--primary-color-text)"
                                : "",
                          }}
                          noTooltip={true}
                          value={formatSlotTime(currentValues[`req${idx + 1}_startTime`]) || ""}
                          disabled={isDisabled}
                          onChange={(val) =>
                            handleInputChange(
                              { target: { value: val } },
                              `req${idx + 1}_startTime`,
                              item,
                              matchSlotsCount,
                            )
                          }
                          height={32}
                          type="startTime"
                          minHr={listingType === "GREATER_THAN_SEVEN_DAYS" ? 3 : undefined}
                          startDate={listingType === "GREATER_THAN_SEVEN_DAYS" ? currentValues[`req${idx + 1}_startDate`] : undefined}
                        />
                      </div>
                    </div>

                    {/* End Time */}
                    <div className={Styles.row}>
                      <div
                        style={{
                          flexBasis: "191px",
                          minWidth: "191px",
                          maxWidth: "191px",
                          width: "191px",
                        }}
                      >
                        End Time
                      </div>
                      <div>{formatSlotTime(selfSlot.slotEndAt)}</div>
                      <div>{formatSlotTime(matchSlot.slotEndAt)}</div>
                      <div style={{ display: "flex", justifyContent: "end" }}>
                        <TimeInput
                          style={{
                            width: "98%",
                            border:
                              isDisabled &&
                              String(formatSlotTime(matchSlot.slotEndAt)) !==
                                String(
                                  formatSlotTime(currentValues[`req${idx + 1}_endTime`]),
                                )
                                ? "2px solid var(--primary-color-text)"
                                : fieldErrors[`req${idx + 1}_endTime`]
                                  ? "2px solid #d32f2f"
                                  : "",
                          }}
                          value={formatSlotTime(currentValues[`req${idx + 1}_endTime`]) || ""}
                          disabled={isDisabled}
                          onChange={(val) => {
                            const errorKey = `req${idx + 1}_endTime`;
                            if (val && val > "24:00") {
                              setFieldErrors((prev) => ({
                                ...prev,
                                [errorKey]: "End Time cannot be after 24:00",
                              }));
                              return;
                            }
                            setFieldErrors((prev) => {
                              const next = { ...prev };
                              delete next[errorKey];
                              return next;
                            });
                            handleInputChange(
                              { target: { value: val } },
                              `req${idx + 1}_endTime`,
                              item,
                              matchSlotsCount,
                            );
                          }}
                          height={32}
                          type="endTime"
                          startTime={currentValues[`req${idx + 1}_startTime`] || ""}
                        />
                        {fieldErrors[`req${idx + 1}_endTime`] && (
                          <div style={{ color: "#d32f2f", fontSize: "12px" }} />
                        )}
                      </div>
                    </div>
                  </>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CounterpartySection;
